import { API_BASE_URL } from './config';
import { authHeadersForGet, authHeadersJson } from './http';

export type CredentialChannelType = 'meta' | 'whatsapp_web';

export type CredentialProvider =
  | 'meta'
  | 'whatsapp_web_js'
  | 'baileys';

export interface MessagesCredentialStatus {
  reference: string;
  credentialType?: CredentialChannelType;
  provider: CredentialProvider | 'whatsapp_web';
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  businessPhoneE164: string;
  lastError: string | null;
  metaPhoneNumberId?: string;
  metaTokenConfigured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCredentialRequest {
  credentialType: CredentialChannelType;
  provider: CredentialProvider;
  businessPhoneE164: string;
  metaAccessToken?: string;
  metaPhoneNumberId?: string;
  metaVerifyToken?: string;
  metaGraphVersion?: string;
}

export interface ValidateNumberRequest {
  credentialType: CredentialChannelType;
  provider: CredentialProvider;
  businessPhoneE164: string;
}

export const messagesCredentialsApi = {
  async getStatus(): Promise<MessagesCredentialStatus[]> {
    const response = await fetch(`${API_BASE_URL}/messages-credentials/status`, {
      headers: authHeadersForGet(),
    });
    if (!response.ok) throw new Error('Failed to get credentials status');
    return response.json();
  },

  async createCredential(data: CreateCredentialRequest): Promise<MessagesCredentialStatus> {
    const response = await fetch(`${API_BASE_URL}/messages-credentials`, {
      method: 'POST',
      headers: authHeadersJson(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create credential');
    }
    return response.json();
  },

  async validateNumber(data: ValidateNumberRequest): Promise<{ allowed: boolean; reason?: string }> {
    const response = await fetch(`${API_BASE_URL}/messages-credentials/validate`, {
      method: 'POST',
      headers: authHeadersJson(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to validate number');
    return response.json();
  },

  async updateCredential(reference: string, data: Partial<CreateCredentialRequest>): Promise<MessagesCredentialStatus> {
    const response = await fetch(`${API_BASE_URL}/messages-credentials/${reference}`, {
      method: 'PUT',
      headers: authHeadersJson(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update credential');
    return response.json();
  },

  async deleteCredential(reference: string): Promise<boolean> {
    const response = await fetch(`${API_BASE_URL}/messages-credentials/${reference}`, {
      method: 'DELETE',
      headers: authHeadersForGet(),
    });
    if (!response.ok) throw new Error('Failed to delete credential');
    return true;
  },
};
